/**
 * BuildingInteriorModule — walkable building interiors, the FOUNDATION
 * (2026-08-13). The ship-deck doctrine on the ground:
 *
 *   · Eligible buildings (living quarters, civic halls, guild-scale — the
 *     enterables registry, fed by the settlement builder and the deed
 *     renderer) grow a door prompt: E at the door steps inside.
 *   · Entering HIDES the building's exterior (its instances + collider) and
 *     builds a SAME-SCENE procedural shell inside the real footprint at the
 *     real position: floors every 3.4 m up the building's true height,
 *     stair runs between them, windowed perimeter walls — the glass panes
 *     are true openings, so the view out IS the street you walked in from.
 *   · The KCC walks it all on plain box colliders (structure-floor
 *     grounding is existing physics law); movement stays the normal
 *     server-validated ground path — no world hop, no teleport.
 *   · E at the door (or straying far from the building) steps out and
 *     restores the exterior exactly.
 *
 * Interior surfaces are MeshBasicMaterial (the baked-materials law: interior
 * readability must never depend on scene lighting).
 */
import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { disposeLater } from '../lib/gpu_dispose.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { Phase, makeSystem } from '../engine-compat.js';
import {
  enterables, doorWorld, interiorSpec, type EnterableBuilding, type InteriorSpec,
} from '../lib/building_interiors.js';
import { FURNITURE, type FurnitureSpec } from '../lib/furniture_catalog.js';
import { furnitureFit } from '../lib/furniture_place.js';
import type { PhysicsModule } from './PhysicsModule.js';
import type { AssetModule } from './AssetModule.js';

const DOOR_RANGE_M = 4.5;
const AUTO_EXIT_M = 26;

// ── Procedural interior textures (no asset deps; unlit per the
// interior-materials law). Near-white canvases so the material color tints
// them: warm planks underfoot, soft seamed panels on the walls. ──
let texCache: { floor: THREE.CanvasTexture; wall: THREE.CanvasTexture } | null = null;
function interiorTextures(): { floor: THREE.CanvasTexture; wall: THREE.CanvasTexture } {
  if (texCache) return texCache;
  const make = (draw: (g: CanvasRenderingContext2D) => void): THREE.CanvasTexture => {
    const cv = document.createElement('canvas');
    cv.width = 256; cv.height = 256;
    draw(cv.getContext('2d')!);
    const t = new THREE.CanvasTexture(cv);
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.anisotropy = 4;
    return t;
  };
  const floor = make((g) => {
    g.fillStyle = '#cbb79b';
    g.fillRect(0, 0, 256, 256);
    for (let row = 0; row < 4; row++) {
      const y0 = row * 64;
      const off = (row % 2) * 128;
      for (let i = -1; i < 3; i++) {
        const x0 = off + i * 128;
        const tone = 195 + ((row * 7 + i * 13) % 5) * 8;
        g.fillStyle = `rgb(${tone},${tone - 22},${tone - 48})`;
        g.fillRect(x0 + 2, y0 + 2, 124, 60);
        g.fillStyle = 'rgba(90,70,50,0.25)';
        for (let k = 0; k < 5; k++) g.fillRect(x0 + 8 + ((row * 31 + k * 47 + i * 17) % 108), y0 + 6 + (k * 11) % 50, 22, 1);
      }
    }
  });
  const wall = make((g) => {
    g.fillStyle = '#d7dbe2';
    g.fillRect(0, 0, 256, 256);
    for (let px = 0; px < 2; px++) {
      for (let py = 0; py < 2; py++) {
        const tone = 210 + ((px * 3 + py * 5) % 3) * 6;
        g.fillStyle = `rgb(${tone},${tone + 2},${tone + 8})`;
        g.fillRect(px * 128 + 3, py * 128 + 3, 122, 122);
        g.strokeStyle = 'rgba(70,80,100,0.35)';
        g.lineWidth = 2;
        g.strokeRect(px * 128 + 3, py * 128 + 3, 122, 122);
        g.fillStyle = 'rgba(120,130,150,0.5)';
        g.fillRect(px * 128 + 10, py * 128 + 10, 3, 3);
        g.fillRect(px * 128 + 115, py * 128 + 10, 3, 3);
        g.fillRect(px * 128 + 10, py * 128 + 115, 3, 3);
        g.fillRect(px * 128 + 115, py * 128 + 115, 3, 3);
      }
    }
  });
  texCache = { floor, wall };
  return texCache;
}

const MATS = {
  wall: () => new THREE.MeshBasicMaterial({ color: 0x9aa3b5, map: interiorTextures().wall }),
  floor: () => new THREE.MeshBasicMaterial({ color: 0xb0937a, map: interiorTextures().floor }),
  stair: () => new THREE.MeshBasicMaterial({ color: 0x6e5c48, map: interiorTextures().floor }),
  trim: () => new THREE.MeshBasicMaterial({ color: 0xffd9a0, toneMapped: false }),
  glass: () => Object.assign(new THREE.MeshBasicMaterial({ color: 0x9fd4ff, transparent: true, opacity: 0.14, side: THREE.DoubleSide, depthWrite: false }), { name: 'interior-glass' }), // named: the area lights find panes by material name (render wave 1)
};

export class BuildingInteriorModule implements IModule {
  readonly name = 'BuildingInterior';
  private scene!: THREE.Scene;
  private active: { b: EnterableBuilding; spec: InteriorSpec; group: THREE.Group; cids: string[]; entryDist: number; releases: Array<() => void> } | null = null;
  private prompt: HTMLDivElement | null = null;
  private onKey: ((e: KeyboardEvent) => void) | null = null;
  private nearDoorId: string | null = null;
  private acc = 0;

  constructor(private readonly physics: PhysicsModule, private readonly assets?: AssetModule) {}

  init(app: App): void {
    this.scene = app.world.scene;
    const prompt = document.createElement('div');
    prompt.id = 'interior-prompt';
    prompt.style.cssText = 'position:fixed;left:50%;bottom:228px;transform:translateX(-50%);z-index:41;display:none;'
      + 'padding:6px 14px;border-radius:8px;background:rgba(12,18,28,0.85);border:1px solid #35506a;'
      + 'font:600 13px system-ui;color:#cfe3ff;pointer-events:none';
    document.body.appendChild(prompt);
    this.prompt = prompt;
    this.onKey = (e: KeyboardEvent): void => {
      if (e.code !== 'KeyE' || e.ctrlKey || e.metaKey || e.repeat) return; // repeat guard: holding E must not toggle enter/exit
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA')) return;
      if (this.active) { if (this.nearDoorId === 'exit') this.exit(); return; }
      if (this.nearDoorId) { const b = enterables.get(this.nearDoorId); if (b) this.enter(b); }
    };
    window.addEventListener('keydown', this.onKey);
    app.world.addSystem(makeSystem('BuildingInterior', Phase.UI, 34, () => this.tick()));
    // Dev hooks (probes) — list/enter/exit without walking.
    (window as unknown as Record<string, unknown>).__interior = {
      list: (): Array<{ id: string; name: string; w: number; h: number; d: number; floors: number; x: number; z: number; groundY: number; yaw: number }> =>
        [...enterables.values()].map((b) => ({ id: b.id, name: b.name, w: Math.round(b.w), h: Math.round(b.h), d: Math.round(b.d), floors: interiorSpec(b.w, b.h, b.d).floors, x: b.x, z: b.z, groundY: b.groundY, yaw: b.yaw })),
      enter: (id: string): boolean => { const b = enterables.get(id); if (!b || this.active) return false; this.enter(b); return true; },
      exit: (): boolean => { if (!this.active) return false; this.exit(); return true; },
      state: (): { inside: string | null; floors: number; colliders: number; panes: number } | null => this.active
        ? { inside: this.active.b.id, floors: this.active.spec.floors, colliders: this.active.cids.length, panes: this.active.spec.panes.length }
        : { inside: null, floors: 0, colliders: 0, panes: 0 },
    };
  }

  // ── Exterior door markers: a warm glowing frame on every ENTERABLE door so
  // players can tell from the street which buildings open (and which don't).
  private readonly doorMarkers = new Map<string, THREE.Group>();
  private markerMats: { jamb: THREE.MeshBasicMaterial; portal: THREE.MeshBasicMaterial } | null = null;
  private markerAcc = 0;

  private markersTick(): void {
    if (++this.markerAcc % 60 !== 0) return; // ~1 Hz — doors don't move
    const px = atlas.localX, pz = atlas.localZ;
    const RANGE = 130;
    this.markerMats ??= {
      jamb: new THREE.MeshBasicMaterial({ color: 0xffd9a0, toneMapped: false }),
      portal: new THREE.MeshBasicMaterial({ color: 0xffb85c, transparent: true, opacity: 0.22, side: THREE.DoubleSide, depthWrite: false, toneMapped: false }),
    };
    const live = new Set<string>();
    for (const b of enterables.values()) {
      if (Math.abs(b.x - px) > RANGE || Math.abs(b.z - pz) > RANGE) continue;
      if (this.active?.b.id === b.id) continue; // exterior hidden — no frame
      live.add(b.id);
      if (this.doorMarkers.has(b.id)) continue;
      const dw = doorWorld(b);
      const gI = new THREE.Group();
      const jambGeo = new THREE.BoxGeometry(0.18, 2.6, 0.18);
      const lintelGeo = new THREE.BoxGeometry(2.26, 0.18, 0.18);
      const l = new THREE.Mesh(jambGeo, this.markerMats.jamb); l.position.set(-1.04, 1.3, 0);
      const r = new THREE.Mesh(jambGeo, this.markerMats.jamb); r.position.set(1.04, 1.3, 0);
      const top = new THREE.Mesh(lintelGeo, this.markerMats.jamb); top.position.set(0, 2.69, 0);
      const portal = new THREE.Mesh(new THREE.PlaneGeometry(1.9, 2.6), this.markerMats.portal);
      portal.position.set(0, 1.3, 0);
      gI.add(l, r, top, portal);
      // Sit at the door, facing outward, nudged off the facade (no z-fight).
      gI.position.set(dw.x, b.groundY, dw.z);
      gI.rotation.y = b.yaw;
      gI.translateZ(-0.22);
      this.scene.add(gI);
      this.doorMarkers.set(b.id, gI);
    }
    for (const [id, gM] of this.doorMarkers) {
      if (live.has(id)) continue;
      this.scene.remove(gM);
      gM.traverse((o) => { const m = o as THREE.Mesh; if (m.geometry) disposeLater(m.geometry); });
      this.doorMarkers.delete(id);
    }
  }

  private tick(): void {
    this.markersTick();
    if (++this.acc % 10 !== 0) return; // ~6 Hz scan
    // Interiors live wherever enterables are registered: the Commons AND the
    // Merchant Hive (its 172 leaseable stores are all EnterableBuildings —
    // the commons-only gate silently disabled E-enter on the hive).
    const inMerchantHive = (() => {
      try {
        if (atlas.currentWorldId === null || !atlas.conn) return false;
        for (const w of atlas.conn.db.world.iter()) {
          if (w.kind === 'merchant') return w.worldId === atlas.currentWorldId;
        }
      } catch { /* table mid-churn */ }
      return false;
    })();
    const inPersonal = (() => {
      try {
        if (atlas.currentWorldId === null || !atlas.conn) return false;
        for (const w of atlas.conn.db.world.iter()) {
          if (w.worldId === atlas.currentWorldId) return w.kind === 'personal';
        }
      } catch { /* mid-churn */ }
      return false;
    })();
    const onGround = atlas.currentWorldId !== null && !atlas.spaceFlight
      && (atlas.currentWorldId === atlas.commonsWorldId || inMerchantHive || inPersonal);
    if (!onGround) {
      if (this.active) this.exit(); // left the world (teleport/space) — restore
      if (this.prompt) this.prompt.style.display = 'none';
      return;
    }
    const px = atlas.localX, pz = atlas.localZ;
    if (this.active) {
      // Settlement rebuild mid-visit (LOD flip / re-stream): the OLD design
      // exited the interior here — which DELETED every interior collider
      // while the player stood mid-stairs (glitch through the steps, limbo,
      // terrain clamp + restored exterior = ejected: the whole field-bug
      // trio, re-created every re-assembly and bypassing all the geometry
      // fixes). The interior now PERSISTS through rebuilds: the shell and
      // colliders stay alive; when the fresh registry entry appears we
      // re-hide the rebuilt exterior and adopt its closures for the eventual
      // restore. While the entry is missing (mid-rebuild) we simply hold.
      const cur = enterables.get(this.active.b.id);
      if (!cur) {
        if (this.prompt) this.prompt.style.display = 'none';
        return; // mid-rebuild — hold; interior geometry remains solid
      }
      if (cur !== this.active.b) {
        cur.hide();            // strip the rebuilt exterior + its collider
        this.active.b = cur;   // fresh closures own the eventual restore
      }
      const b = this.active.b;
      const dw = doorWorld(b);
      const dDoor = Math.hypot(dw.x - px, dw.z - pz);
      const dCenter = Math.hypot(b.x - px, b.z - pz);
      // Walked OUT (door gap or any other way past the walls) → restore the
      // exterior IMMEDIATELY: the shell must never be seen standing in the
      // street ("the building was replaced" bug). Local-frame test against
      // the shell's own core bounds; a margin avoids doorway flicker.
      const c = Math.cos(b.yaw), sn = Math.sin(b.yaw);
      const dx = px - b.x, dz = pz - b.z;
      const lx = c * dx - sn * dz, lz = sn * dx + c * dz;
      // Real door entries start within the walkable CORE (clamped bounds —
      // the raw footprint of a km-bbox landmark would count remote dev-hook
      // entries as "at the door" and instantly threshold-exit them).
      const enteredAtDoor = this.active.entryDist < Math.hypot(this.active.spec.hw, this.active.spec.hd) + 4;
      // Margin clears the 1.6 m thick wall/door tunnel before restoring.
      const walkedOut = enteredAtDoor
        && (Math.abs(lx) > this.active.spec.hw + 1.9 || Math.abs(lz) > this.active.spec.hd + 1.9);
      // Strayed — restore the world. The threshold is entry-distance aware:
      // a real player enters at the door (entryDist ≈ footprint), but the
      // __interior dev hook enters from anywhere — a fixed radius would
      // auto-exit the very next tick and read as "enter never happened".
      const exitAt = Math.max(Math.max(b.w, b.d) / 2 + AUTO_EXIT_M, this.active.entryDist + 12);
      if (walkedOut || dCenter > exitAt) { this.exit(); return; }
      this.nearDoorId = dDoor < DOOR_RANGE_M ? 'exit' : null;
      if (this.prompt) {
        this.prompt.style.display = this.nearDoorId ? 'block' : 'none';
        this.prompt.textContent = `E — leave ${this.active.b.name}`;
      }
      return;
    }
    // Nearest enterable door within reach.
    let bestId: string | null = null;
    let bestD = DOOR_RANGE_M;
    for (const b of enterables.values()) {
      if (Math.abs(b.x - px) > 60 || Math.abs(b.z - pz) > 60) continue; // cheap reject
      const dw = doorWorld(b);
      const d = Math.hypot(dw.x - px, dw.z - pz);
      if (d < bestD) { bestD = d; bestId = b.id; }
    }
    this.nearDoorId = bestId;
    if (this.prompt) {
      if (bestId) {
        const b = enterables.get(bestId)!;
        const floors = interiorSpec(b.w, b.h, b.d).floors;
        this.prompt.style.display = 'block';
        this.prompt.textContent = `E — enter ${b.name}${floors > 1 ? ` (${floors} floors)` : ''}`;
      } else {
        this.prompt.style.display = 'none';
      }
    }
  }

  private enter(b: EnterableBuilding): void {
    const spec = interiorSpec(b.w, b.h, b.d, b.key);
    const group = new THREE.Group();
    group.name = 'building-interior';
    const cos = Math.cos(b.yaw), sin = Math.sin(b.yaw);
    const toWorld = (lx: number, lz: number): { x: number; z: number } =>
      ({ x: b.x + lx * cos + lz * sin, z: b.z - lx * sin + lz * cos });
    const mats = { wall: MATS.wall(), floor: MATS.floor(), stair: MATS.stair(), trim: MATS.trim(), glass: MATS.glass() };
    const cids: string[] = [];
    let ci = 0;
    for (const box of spec.boxes) {
      if (box.render !== false) {
        const geo = new THREE.BoxGeometry(box.w, box.h, box.d);
        // World-scale tiling (~2.5 m per texture tile): BoxGeometry UVs are
        // 0..1 per face, so scale them by the box's dominant-face dims.
        if (box.mat === 'floor' || box.mat === 'wall' || box.mat === 'stair') {
          const su = Math.max(box.w, box.d) / 2.5;
          const sv = (box.mat === 'floor' ? Math.min(box.w, box.d) : Math.max(box.h, 0.4)) / 2.5;
          const uv = geo.attributes.uv as THREE.BufferAttribute;
          for (let i = 0; i < uv.count; i++) uv.setXY(i, uv.getX(i) * su, uv.getY(i) * sv);
          uv.needsUpdate = true;
        }
        const mesh = new THREE.Mesh(geo, mats[box.mat]);
        const wp = toWorld(box.x, box.z);
        mesh.position.set(wp.x, b.groundY + box.y, wp.z);
        mesh.rotation.y = b.yaw;
        group.add(mesh);
      }
      if (box.collide) {
        const wp = toWorld(box.x, box.z);
        const cid = `int:${b.id}:${ci++}`;
        this.physics.addStaticCollider(cid, { shape: 'box', halfExtents: [box.w / 2, box.h / 2, box.d / 2] },
          { x: wp.x, y: b.groundY + box.y, z: wp.z, yaw: b.yaw, scale: 1 });
        cids.push(cid);
      }
    }
    for (const pane of spec.panes) {
      const geo = new THREE.PlaneGeometry(pane.w, pane.h);
      const mesh = new THREE.Mesh(geo, mats.glass);
      const wp = toWorld(pane.x, pane.z);
      mesh.position.set(wp.x, b.groundY + pane.y, wp.z);
      // Pane faces its wall normal: x-axis walls face ±X (rotate 90°).
      mesh.rotation.y = b.yaw + (pane.axis === 'x' ? Math.PI / 2 : 0);
      group.add(mesh);
    }
    group.userData.interiorId = b.id; // the pane-ownership tag the area lights read (render wave 1, 2026-09-17)
    this.scene.add(group);
    b.hide();
    const entryDist = Math.hypot(b.x - atlas.localX, b.z - atlas.localZ);
    this.active = { b, spec, group, cids, entryDist, releases: [] };
    atlas.interiorId = b.id;
    // Versona homes (settlement buildings) arrive furnished — the versona
    // smiths craft from the same slate players do (crafting_ops events tell
    // the story); the layout is deterministic per building.
    if (b.id.startsWith('settle:') || b.id.startsWith('preset:')) void this.furnishVersonaHome(b, spec, group, toWorld);
    else if (b.key === 'deed:guild_hall') void this.furnishGuildHall(b, spec, group, toWorld);
    else if (b.key === 'hive:store') void this.furnishHiveStore(b, spec, group, toWorld);
    bus.emit('atlas:status', { text: `${b.name} — ${spec.floors} floor${spec.floors > 1 ? 's' : ''}; the stairs climb from the west wall. E at the door to leave. [r7]` });
  }

  /** Deterministic furnished layout for a versona home: a few pieces from the
   *  shared furniture slate along the walls + center of each lower floor. */
  private async furnishVersonaHome(
    b: EnterableBuilding, spec: InteriorSpec, group: THREE.Group,
    toWorld: (lx: number, lz: number) => { x: number; z: number },
  ): Promise<void> {
    const conn = atlas.conn;
    const assets = this.assets;
    if (!conn || !assets) return;
    // SESSION IDENTITY, not building id: exit + re-enter the same home while
    // this pass awaits a load made the old pass furnish the dead group and
    // push its releases onto the new session (the home stayed bare).
    const session = this.active;
    if (!session) return;
    // Seed from the building id — stable per home, varied across the street.
    let h = 0x811c9dc5;
    for (let i = 0; i < b.id.length; i++) { h ^= b.id.charCodeAt(i); h = Math.imul(h, 0x01000193); }
    const rnd = (): number => { h ^= h << 13; h ^= h >>> 17; h ^= h << 5; return ((h >>> 0) % 1000) / 1000; };
    // ── Layout by interior-design rules (web research 2026-08-14): keep the
    // primary walkways clear (≥0.9 m door→stairs corridor), anchor pieces on
    // walls FACING the room (sofa perpendicular to circulation), keep the
    // conversation grouping ~1.4 m (sofa→table), never block the doorway or
    // the stair approach. Floors are THEMED: living below, sleeping above,
    // study/den on the third — a believable home, not scattered props. ──
    const hwI = spec.hw, hdI = spec.hd;
    const st = spec.stair;
    const clearOf = (x: number, z: number): boolean => {
      // Door corridor: from the -Z door straight in.
      if (Math.abs(x) < 1.4 && z < -hdI + 3.4) return false;
      // Stair zone + its approach.
      if (x > st.x - 2.0 && x < st.x + 2.0 && z > st.z - st.d / 2 - 1.8 && z < st.z + st.d / 2 + 1.8) return false;
      // Inside the room bounds with wall clearance.
      return Math.abs(x) < hwI - 0.55 && Math.abs(z) < hdI - 0.55;
    };
    const pickCat = (cat: FurnitureSpec['cat']): string => {
      const pool = Object.values(FURNITURE).filter((s) => s.cat === cat);
      return pool[Math.floor(rnd() * pool.length) % pool.length].key;
    };
    // Per-floor themed slot plans (local coords; yaw faces the room).
    type Slot = { key: string; x: number; z: number; yaw: number };
    const planFor = (f: number): Slot[] => {
      const s: Slot[] = [];
      if (f === 0) { // living: sofa on the +X wall facing in, table 1.4 m out
        s.push({ key: pickCat('seating'), x: hwI - 1.0, z: -hdI * 0.25, yaw: -Math.PI / 2 });
        s.push({ key: 'table_low', x: hwI - 2.45, z: -hdI * 0.25, yaw: -Math.PI / 2 });
        s.push({ key: 'rug_woven', x: hwI - 2.4, z: -hdI * 0.25, yaw: -Math.PI / 2 });
        s.push({ key: pickCat('utility'), x: 1.8, z: hdI - 0.95, yaw: Math.PI });
        s.push({ key: pickCat('light'), x: hwI - 0.8, z: hdI - 0.8, yaw: Math.PI * 0.75 });
        s.push({ key: pickCat('decor'), x: 2.3, z: -hdI + 1.0, yaw: 0 });
      } else if (f === 1) { // sleeping: bed on the +Z wall, storage opposite
        s.push({ key: rnd() < 0.5 ? 'bed_double' : 'bed_single', x: 1.7, z: hdI - 1.35, yaw: Math.PI });
        s.push({ key: pickCat('storage'), x: hwI - 0.85, z: 0.4, yaw: -Math.PI / 2 });
        s.push({ key: 'chest_storage', x: 1.7, z: hdI - 3.3, yaw: Math.PI });
        s.push({ key: 'lamp_table', x: hwI - 0.75, z: hdI - 0.85, yaw: Math.PI * 0.75 });
        s.push({ key: 'rug_woven', x: 1.7, z: hdI * 0.25, yaw: 0 });
      } else { // den/study: desk on the +Z wall, shelves, greenery
        s.push({ key: rnd() < 0.5 ? 'desk_writing' : 'desk_terminal', x: 1.8, z: hdI - 1.0, yaw: Math.PI });
        s.push({ key: 'chair_dining', x: 1.8, z: hdI - 2.1, yaw: 0 });
        s.push({ key: pickCat('storage'), x: hwI - 0.85, z: -0.4, yaw: -Math.PI / 2 });
        s.push({ key: pickCat('decor'), x: hwI - 0.9, z: hdI - 0.9, yaw: Math.PI * 0.75 });
        s.push({ key: pickCat('light'), x: 2.4, z: -hdI + 1.1, yaw: 0 });
      }
      return s.filter((sl) => clearOf(sl.x, sl.z));
    };
    const floors = Math.min(spec.floors, 3); // furnish the lived-in lower floors
    for (let f = 0; f < floors; f++) {
      const plan = planFor(f);
      for (const slot of plan) {
        const alive = await this.placeSlot(session, b, group, toWorld, slot.key, slot.x, slot.z, slot.yaw, f === 0 ? 0.08 : f * 3.4, (rnd() - 0.5) * 0.12);
        if (!alive) return;
      }
    }
  }

  /** Load one furniture prefab and seat it in the active interior. Returns
   *  false when the player left mid-load (the caller stops furnishing). */
  private async placeSlot(
    session: NonNullable<BuildingInteriorModule['active']>, b: EnterableBuilding, group: THREE.Group,
    toWorld: (lx: number, lz: number) => { x: number; z: number },
    key: string, x: number, z: number, yaw: number, floorY: number, jitter = 0,
  ): Promise<boolean> {
    const conn = atlas.conn;
    const assets = this.assets;
    if (!conn || !assets) return false;
    const fspec: FurnitureSpec | undefined = FURNITURE[key];
    const pm = [...conn.db.prefabModel.iter()].find((p) => p.prefabKey === `prop:furn_${key}`);
    if (!fspec || !pm) return true; // model not generated yet — skip quietly
    try {
      const { object: master, release } = await assets.loadForTier(pm.assetId);
      // The player may have left (or re-entered) while loading.
      if (this.active !== session) { release(); return false; }
      session.releases.push(release);
      const bb = new THREE.Box3().setFromObject(master);
      const { scale, yOff } = furnitureFit(fspec, bb);
      const clone = master.clone(true);
      // Geometry is SHARED with the asset cache — exit() must release the
      // handle, never dispose it (the tag skips disposal). Materials are
      // REPLACED with unlit basics (the interior-materials law: interior
      // readability never depends on scene lighting — PBR goes black at
      // night); those are ours, disposed via the releases list.
      clone.traverse((o) => {
        o.userData.sharedAsset = true;
        const mesh = o as THREE.Mesh;
        if ((mesh as { isMesh?: boolean }).isMesh && mesh.material) {
          const src = mesh.material as THREE.MeshStandardMaterial;
          const basic = new THREE.MeshBasicMaterial({ map: src.map ?? null, color: 0xffffff });
          mesh.material = basic;
          this.active?.releases.push(() => disposeLater(basic)); // (bug sweep) exit() drains releases synchronously — a drawn material freed inside the submit window
        }
      });
      const wp = toWorld(x, z);
      clone.position.set(wp.x, b.groundY + floorY + yOff, wp.z);
      clone.rotation.y = b.yaw + yaw + jitter;
      clone.scale.setScalar(scale);
      group.add(clone);
    } catch { /* asset missing — leave the slot bare */ }
    return true;
  }

  /** A Merchant Hive premium store: warm retail — service counter, display
   *  shelving, holo displays, greenery at the door. Deterministic. */
  private async furnishHiveStore(
    b: EnterableBuilding, spec: InteriorSpec, group: THREE.Group,
    toWorld: (lx: number, lz: number) => { x: number; z: number },
  ): Promise<void> {
    const session = this.active;
    if (!session) return;
    const hw = spec.hw, hd = spec.hd;
    const plan: Array<{ key: string; x: number; z: number; yaw: number }> = [
      { key: 'counter_bar', x: -hw * 0.18, z: hd - 1.15, yaw: Math.PI },
      { key: 'screen_holo', x: hw * 0.55, z: hd - 1.1, yaw: Math.PI },
      { key: 'shelf_unit', x: -hw + 1.0, z: -hd * 0.35, yaw: Math.PI / 2 },
      { key: 'shelf_unit', x: hw - 1.0, z: -hd * 0.35, yaw: -Math.PI / 2 },
      { key: 'lamp_floor', x: -hw + 1.0, z: -hd + 1.2, yaw: Math.PI / 4 },
      { key: 'lamp_floor', x: hw - 1.0, z: -hd + 1.2, yaw: -Math.PI / 4 },
      { key: 'plant_small', x: 2.0, z: -hd + 1.0, yaw: 0 },
      { key: 'rug_woven', x: 0, z: -hd * 0.15, yaw: 0 },
      { key: 'statue_small', x: 0, z: hd * 0.35, yaw: Math.PI },
    ];
    for (const slot of plan) {
      if (Math.abs(slot.x) > hw - 0.5 || Math.abs(slot.z) > hd - 0.5) continue;
      if (Math.abs(slot.x) < 1.4 && slot.z < -hd + 3.4) continue; // door corridor
      const alive = await this.placeSlot(session, b, group, toWorld, slot.key, slot.x, slot.z, slot.yaw, 0.08);
      if (!alive) return;
    }
  }

  /** The guild hall's bespoke interior (2026-08-29): a columned GREAT HALL —
   *  long charter tables and benches down the nave toward the dais, a hearth
   *  and bar for the members — then a COUNCIL chamber (floor 1) and the
   *  guild ARCHIVE (floor 2). Deterministic; the structural columns, dais,
   *  and banners come from interiorSpec's guild-hall boxes. */
  private async furnishGuildHall(
    b: EnterableBuilding, spec: InteriorSpec, group: THREE.Group,
    toWorld: (lx: number, lz: number) => { x: number; z: number },
  ): Promise<void> {
    const session = this.active;
    if (!session) return;
    const hw = spec.hw, hd = spec.hd, st = spec.stair;
    const clearOf = (x: number, z: number): boolean => {
      if (Math.abs(x) < 1.4 && z < -hd + 3.4) return false;               // door corridor
      if (x > st.x - 2.0 && x < st.x + 2.0 && z > st.z - st.d / 2 - 1.8 && z < st.z + st.d / 2 + 1.8) return false; // stairs
      return Math.abs(x) < hw - 0.55 && Math.abs(z) < hd - 0.55;
    };
    type Slot = { key: string; x: number; z: number; yaw: number };
    const plans: Slot[][] = [
      [ // floor 0 — the great hall: the charter line runs toward the dais
        { key: 'table_dining', x: 0, z: -hd * 0.28, yaw: Math.PI / 2 },
        { key: 'table_dining', x: 0, z: hd * 0.12, yaw: Math.PI / 2 },
        { key: 'bench_hall', x: 1.7, z: -hd * 0.28, yaw: -Math.PI / 2 },
        { key: 'bench_hall', x: -1.7, z: -hd * 0.28, yaw: Math.PI / 2 },
        { key: 'bench_hall', x: 1.7, z: hd * 0.12, yaw: -Math.PI / 2 },
        { key: 'bench_hall', x: -1.7, z: hd * 0.12, yaw: Math.PI / 2 },
        { key: 'rug_woven', x: 0, z: -hd * 0.08, yaw: Math.PI / 2 },
        { key: 'stove_hearth', x: hw - 1.3, z: hd - 3.3, yaw: -Math.PI / 2 },
        { key: 'counter_bar', x: -hw * 0.5, z: hd - 1.15, yaw: Math.PI },
        { key: 'light_column', x: hw * 0.6, z: -hd + 4.2, yaw: 0 },
        { key: 'light_column', x: -hw * 0.6, z: hd - 4.6, yaw: 0 },
        { key: 'statue_small', x: 2.3, z: -hd + 1.2, yaw: 0 },
        { key: 'statue_small', x: -2.3, z: -hd + 1.2, yaw: 0 },
        { key: 'plant_large', x: hw - 1.1, z: -hd + 1.5, yaw: 0 },
        { key: 'screen_holo', x: 2.6, z: hd - 3.4, yaw: Math.PI },
      ],
      [ // floor 1 — the council chamber: a ring around one table
        { key: 'table_dining', x: 0.6, z: 0.4, yaw: 0 },
        { key: 'chair_dining', x: 0.6, z: -1.1, yaw: 0 },
        { key: 'chair_dining', x: 0.6, z: 1.9, yaw: Math.PI },
        { key: 'chair_dining', x: 2.2, z: 0.4, yaw: -Math.PI / 2 },
        { key: 'chair_dining', x: -1.0, z: 0.4, yaw: Math.PI / 2 },
        { key: 'rug_woven', x: 0.6, z: 0.4, yaw: 0 },
        { key: 'bookcase_arch', x: hw - 0.95, z: 1.6, yaw: -Math.PI / 2 },
        { key: 'screen_holo', x: 0.6, z: hd - 1.1, yaw: Math.PI },
        { key: 'lamp_floor', x: -2.6, z: hd - 1.2, yaw: Math.PI * 0.75 },
        { key: 'plant_small', x: hw - 0.9, z: -hd + 1.2, yaw: 0 },
      ],
      [ // floor 2 — the guild archive: records, terminals, the quiet work
        { key: 'bookcase_arch', x: hw - 0.95, z: 2.2, yaw: -Math.PI / 2 },
        { key: 'bookcase_arch', x: hw - 0.95, z: -1.4, yaw: -Math.PI / 2 },
        { key: 'desk_terminal', x: 1.8, z: hd - 1.05, yaw: Math.PI },
        { key: 'chair_dining', x: 1.8, z: hd - 2.15, yaw: 0 },
        { key: 'desk_writing', x: -3.0, z: hd - 1.05, yaw: Math.PI },
        { key: 'chair_dining', x: -3.0, z: hd - 2.15, yaw: 0 },
        { key: 'cabinet_low', x: 2.6, z: -hd + 1.1, yaw: 0 },
        { key: 'terrarium_pod', x: hw - 1.0, z: -hd + 1.4, yaw: 0 },
        { key: 'lamp_floor', x: -2.4, z: -hd + 1.2, yaw: Math.PI / 4 },
      ],
    ];
    const floors = Math.min(spec.floors, plans.length);
    for (let f = 0; f < floors; f++) {
      for (const slot of plans[f]) {
        if (!clearOf(slot.x, slot.z)) continue;
        const alive = await this.placeSlot(session, b, group, toWorld, slot.key, slot.x, slot.z, slot.yaw, f === 0 ? 0.08 : f * 3.4);
        if (!alive) return;
      }
    }
  }

  private exit(skipRestore = false): void {
    const a = this.active;
    if (!a) return;
    this.scene.remove(a.group);
    // Deferred (warp-teardown law): pressing E to leave is a mid-frame teardown
    // of geometry the in-flight submit may still reference.
    a.group.traverse((o) => {
      if (o.userData.sharedAsset) return; // asset-cache geometry — release only
      const m = o as THREE.Mesh;
      if (m.geometry) disposeLater(m.geometry);
    });
    // Shared per-entry materials — dispose each once (never the asset cache's).
    const seen = new Set<THREE.Material>();
    a.group.traverse((o) => {
      if (o.userData.sharedAsset) return;
      const m = (o as THREE.Mesh).material as THREE.Material | undefined;
      if (m && !seen.has(m)) { seen.add(m); disposeLater(m); }
    });
    for (const rel of a.releases) rel();
    for (const cid of a.cids) this.physics.removeStaticCollider(cid);
    if (!skipRestore) a.b.restore();
    this.active = null;
    atlas.interiorId = null;
    this.nearDoorId = null;
  }

  dispose(): void {
    this.exit();
    if (this.onKey) window.removeEventListener('keydown', this.onKey);
    this.onKey = null;
    this.prompt?.remove();
    for (const [, gM] of this.doorMarkers) {
      this.scene.remove(gM);
      gM.traverse((o) => { const m = o as THREE.Mesh; if (m.geometry) m.geometry.dispose(); });
    }
    this.doorMarkers.clear();
    this.markerMats?.jamb.dispose();
    this.markerMats?.portal.dispose();
  }
}
